import { useState } from "react";
import { useCustom, useCustomMutation, useTranslate } from "@refinedev/core";
import { Button, Modal, TextareaField } from "@fawkes/ui";

/**
 * De bijzonderheden van een dier wijzigen: de labels en de vrije notitie, precies wat de tegel
 * Bijzonderheden toont.
 *
 * <p><b>Waarom niet via de basiskaart.</b> De {@code PUT} op het dier vervangt de hele kaart, en wat je
 * weglaat wis je -- ook velden die dit venster niet kent. De api heeft daarom een eigen, smalle
 * schrijfactie voor alleen deze twee velden ({@code PUT animals/{id}/bijzonderheden}).
 *
 * <p>De labels typ je zoals je ze leest: één per regel, of met komma's ertussen. De api maakt er de
 * vaste vorm van (ontdubbeld, met een komma ertussen), dezelfde die de tag-dialoog op de basiskaart
 * schrijft, zodat beide schermen elkaars labels blijven lezen.
 *
 * <p>Het {@code version}-token komt van het dier zelf. Wie tussendoor iets aan het dier wijzigde,
 * krijgt een 409 en de vraag om opnieuw te openen, in plaats van stil overschreven te worden.
 */
export function BijzonderhedenWijzigen({ dierId, kenmerken, notitie, onSluit, onOpgeslagen }: {
  dierId: number;
  /** De labels zoals de tegel ze toont. */
  kenmerken: string[];
  notitie: string | null;
  onSluit: () => void;
  onOpgeslagen: () => void;
}) {
  const t = useTranslate();
  const { data } = useCustom<{ version: number | null }>({ url: `animals/${dierId}`, method: "get" });
  const version = data?.data?.version ?? null;
  const { mutate, isLoading: bezig } = useCustomMutation();
  const [labels, setLabels] = useState(kenmerken.join("\n"));
  const [tekst, setTekst] = useState(notitie ?? "");
  const [fout, setFout] = useState<string | null>(null);

  const bewaar = () => {
    setFout(null);
    mutate(
      {
        url: `animals/${dierId}/bijzonderheden`,
        method: "put",
        values: {
          kenmerken: labels.trim() === "" ? null : labels,
          bijzonderheden: tekst.trim() === "" ? null : tekst.trim(),
          version,
        },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: onOpgeslagen,
        onError: (e: { statusCode?: number }) =>
          setFout(t(
            e?.statusCode === 409
              ? "pensionKlant.bijzonderheden.conflict"
              : e?.statusCode === 400
                ? "pensionKlant.bijzonderheden.teLang"
                : "pensionKlant.bijzonderheden.fout",
          )),
      },
    );
  };

  return (
    <Modal open title={t("pensionKlant.bijzonderheden.wijzigen")} onClose={onSluit}
      footer={
        <>
          <Button variant="secondary" onClick={onSluit}>{t("common.cancel")}</Button>
          {/* Wachten op het dier: zonder zijn version zou het opslaan een gelijktijdige wijziging
              ongezien overschrijven. */}
          <Button onClick={bewaar} disabled={bezig || !data?.data}>{t("common.save")}</Button>
        </>
      }
    >
      <div className="do-invoer">
        <TextareaField label={t("pensionKlant.bijzonderheden.labels")} value={labels} rows={5}
          data-testid="bijzonderheden-labels" onChange={(e) => setLabels(e.target.value)} />
        <p className="do-invoer__hint">{t("pensionKlant.bijzonderheden.labelsHint")}</p>
        <TextareaField label={t("pensionKlant.bijzonderheden.notitie")} value={tekst} rows={3}
          data-testid="bijzonderheden-notitie" onChange={(e) => setTekst(e.target.value)} />
        {fout ? <p className="do-invoer__fout" role="alert">{fout}</p> : null}
      </div>
    </Modal>
  );
}
